import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { environment } from 'src/environments/environment';

/** Ce que le front relève au moment du signalement. */
export interface BugReportPayload {
  title: string;
  description: string;
  pageUrl: string;
  elementSelector: string;
  componentPath: string;
  userAgent: string;
  viewport: string;
  screenshot: Blob | null;
}

/** Ticket Jira créé par le serveur. */
export interface BugReportResult {
  key: string;
  url: string;
  /** Faux si le ticket existe mais que la capture n'a pas pu y être jointe. */
  screenshotAttached: boolean;
  /** Faux si le ticket existe mais n'a pas pu être placé dans son statut de départ (« À faire »). */
  movedToInitialStatus: boolean;
}

/** Paramètres d'adresse qui portent des secrets : le code des pages de mot de passe, un jeton, une session… */
const SENSITIVE_PARAMETER = /code|token|password|pwd|secret|key|session|auth|state|signature|sig/i;

/**
 * Adresse de la page sans secret, pour le ticket : identifiants et fragment (réponses OAuth)
 * retirés, valeurs des paramètres sensibles masquées — /create-password et /reset-password
 * portent un code en paramètre. Le serveur l'expurge aussi.
 */
export function reportedUrl(href: string): string {
  try {
    const url = new URL(href);
    url.username = '';
    url.password = '';
    url.hash = '';
    for (const name of new Set(url.searchParams.keys())) {
      if (SENSITIVE_PARAMETER.test(name)) {
        url.searchParams.set(name, 'redacted');
      }
    }
    return url.toString();
  } catch {
    return '';
  }
}

/** Message d'échec à traduire, avec ses paramètres. */
export interface BugReportError {
  key: string;
  params?: Record<string, unknown>;
}

/**
 * Message à afficher pour un échec d'envoi. Le 429 porte le délai restant dans son corps :
 * l'API étant sur un autre domaine, le navigateur ne laisse pas lire l'en-tête Retry-After.
 * Une réponse illisible (voir toBugReportResult) donne le message générique.
 */
export function bugReportError(error: unknown): BugReportError {
  if (!(error instanceof HttpErrorResponse)) {
    return { key: 'BUG_REPORT.ERROR' };
  }
  if (error.status === 429) {
    const seconds = Number(error.error?.retryAfterSeconds) || 120;
    return {
      key: 'BUG_REPORT.RATE_LIMITED',
      params: { minutes: Math.floor(seconds / 60), seconds: String(seconds % 60).padStart(2, '0') },
    };
  }
  return { key: error.status === 503 ? 'BUG_REPORT.NOT_CONFIGURED' : 'BUG_REPORT.ERROR' };
}

/**
 * Réponse de l'API vérifiée à l'exécution : le type générique de HttpClient ne contrôle rien, et
 * une réponse inattendue afficherait un ticket sans clé ni lien. Elle part plutôt en erreur.
 */
function toBugReportResult(body: unknown): BugReportResult {
  const value = (body ?? {}) as Record<string, unknown>;
  if (typeof value['key'] === 'string' && value['key'] && typeof value['url'] === 'string' && value['url']
    && typeof value['screenshotAttached'] === 'boolean' && typeof value['movedToInitialStatus'] === 'boolean') {
    return {
      key: value['key'],
      url: value['url'],
      screenshotAttached: value['screenshotAttached'],
      movedToInitialStatus: value['movedToInitialStatus'],
    };
  }
  throw new Error('Unexpected bug report response');
}

@Injectable({ providedIn: 'root' })
export class BugReportService {
  private readonly http = inject(HttpClient);

  send(payload: BugReportPayload): Observable<BugReportResult> {
    const form = new FormData();
    form.append('title', payload.title);
    form.append('description', payload.description);
    form.append('pageUrl', reportedUrl(payload.pageUrl));
    form.append('elementSelector', payload.elementSelector);
    form.append('componentPath', payload.componentPath);
    form.append('userAgent', payload.userAgent);
    form.append('viewport', payload.viewport);
    if (payload.screenshot) {
      form.append('screenshot', payload.screenshot, payload.screenshot.type === 'image/jpeg' ? 'element.jpg' : 'element.png');
    }

    return this.http.post<unknown>(`${environment.exploreURL}/BugReports`, form).pipe(map(toBugReportResult));
  }
}
